import type { ContentVisibility, ServicePluginContext } from "@brains/plugins";
import { z } from "@brains/utils/zod";
import type { ProviderRegistry } from "./provider-registry";
import type { QueueManager } from "./queue-manager";
import type { RetryTracker } from "./retry-tracker";

const publicationStatusSchema = z.enum([
  "draft",
  "queued",
  "published",
  "failed",
]);

type PublicationQueueItemSchema = z.ZodObject<{
  entityId: z.ZodString;
  entityType: z.ZodString;
  title: z.ZodString;
  position: z.ZodNumber;
  queuedAt: z.ZodString;
  destination: z.ZodString;
  scheduledFor: z.ZodOptional<z.ZodString>;
}>;

export const publicationQueueItemSchema: PublicationQueueItemSchema = z.object({
  entityId: z.string(),
  entityType: z.string(),
  title: z.string(),
  position: z.number().int().positive(),
  queuedAt: z.string(),
  destination: z.string(),
  scheduledFor: z.string().optional(),
});

export type PublicationQueueItem = z.output<typeof publicationQueueItemSchema>;

type PublicationJobItemSchema = z.ZodObject<{
  id: z.ZodString;
  label: z.ZodString;
  target: z.ZodString;
  status: z.ZodEnum<{ pending: "pending"; processing: "processing" }>;
}>;

export const publicationJobItemSchema: PublicationJobItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  target: z.string(),
  status: z.enum(["pending", "processing"]),
});

export type PublicationJobItem = z.output<typeof publicationJobItemSchema>;

type PublicationFailureItemSchema = z.ZodObject<{
  entityId: z.ZodString;
  entityType: z.ZodString;
  title: z.ZodString;
  error: z.ZodString;
  retryCount: z.ZodNumber;
}>;

export const publicationFailureItemSchema: PublicationFailureItemSchema =
  z.object({
    entityId: z.string(),
    entityType: z.string(),
    title: z.string(),
    error: z.string(),
    retryCount: z.number().int().nonnegative(),
  });

export type PublicationFailureItem = z.output<
  typeof publicationFailureItemSchema
>;

type PublicationPipelineSnapshotSchema = z.ZodObject<{
  summary: z.ZodObject<{
    draft: z.ZodNumber;
    queued: z.ZodNumber;
    generating: z.ZodNumber;
    failed: z.ZodNumber;
    published: z.ZodNumber;
    needsOperator: z.ZodNumber;
  }>;
  queue: z.ZodArray<PublicationQueueItemSchema>;
  generating: z.ZodArray<PublicationJobItemSchema>;
  failures: z.ZodArray<PublicationFailureItemSchema>;
  publishableEntityTypes: z.ZodArray<z.ZodString>;
}>;

export const publicationPipelineSnapshotSchema: PublicationPipelineSnapshotSchema =
  z.object({
    summary: z.object({
      draft: z.number().int().nonnegative(),
      queued: z.number().int().nonnegative(),
      generating: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      published: z.number().int().nonnegative(),
      needsOperator: z.number().int().nonnegative(),
    }),
    queue: z.array(publicationQueueItemSchema),
    generating: z.array(publicationJobItemSchema),
    failures: z.array(publicationFailureItemSchema),
    publishableEntityTypes: z.array(z.string()),
  });

export type PublicationPipelineSnapshot = z.output<
  typeof publicationPipelineSnapshotSchema
>;

type PublicationStatus = z.output<typeof publicationStatusSchema>;

interface PublicationEntityItem {
  entityId: string;
  entityType: string;
  title: string;
  status: PublicationStatus;
  scheduledFor?: string;
  error?: string;
}

const generatingJobDataSchema = z.object({
  sourceEntityType: z.string(),
  sourceEntityId: z.string(),
  attachmentType: z.string().optional(),
});

interface PublicationPipelineSnapshotOptions {
  visibilityScope?: ContentVisibility;
  entityTypes?: string[];
}

/** Build the content-pipeline-owned read model for operator surfaces. */
export async function getPublicationPipelineSnapshot(
  context: ServicePluginContext,
  providerRegistry: ProviderRegistry,
  queueManager: QueueManager,
  retryTracker: RetryTracker,
  options: PublicationPipelineSnapshotOptions = {},
): Promise<PublicationPipelineSnapshot> {
  const publishableEntityTypes = (
    options.entityTypes ?? providerRegistry.getRegisteredTypes()
  ).sort();
  const entitiesByKey = new Map<string, PublicationEntityItem>();
  const summary = {
    draft: 0,
    queued: 0,
    generating: 0,
    failed: 0,
    published: 0,
    needsOperator: 0,
  };

  for (const entityType of publishableEntityTypes) {
    const entities = await context.entityService.listEntities({
      entityType,
      ...(options.visibilityScope
        ? { options: { filter: { visibilityScope: options.visibilityScope } } }
        : {}),
    });
    for (const entity of entities) {
      const parsedStatus = publicationStatusSchema.safeParse(
        entity.metadata["status"],
      );
      if (!parsedStatus.success) continue;

      summary[parsedStatus.data] += 1;
      const scheduledFor = entity.metadata["scheduledFor"];
      const error = entity.metadata["error"];
      entitiesByKey.set(entityKey(entityType, entity.id), {
        entityId: entity.id,
        entityType,
        title: getEntityTitle(entity.id, entity.metadata),
        status: parsedStatus.data,
        ...(typeof scheduledFor === "string" ? { scheduledFor } : {}),
        ...(typeof error === "string" ? { error } : {}),
      });
    }
  }

  const queue: PublicationPipelineSnapshot["queue"] = [];
  for (const entityType of publishableEntityTypes) {
    const destination = providerRegistry.get(entityType).name;
    let viewPosition = 0;
    for (const entry of await queueManager.list(entityType)) {
      const entity = entitiesByKey.get(entityKey(entityType, entry.entityId));
      if (!entity) continue;
      // Positions are renumbered to the caller's view: a gap left by a
      // filtered-out entry would reveal that something hidden is queued.
      // Reorder actions translate view slots back to absolute positions.
      viewPosition += 1;
      queue.push({
        entityId: entry.entityId,
        entityType,
        title: entity.title,
        position: viewPosition,
        queuedAt: entry.queuedAt,
        destination,
        ...(entity.scheduledFor ? { scheduledFor: entity.scheduledFor } : {}),
      });
    }
  }
  // QueueManager ordering is per publishable type because schedulers consume
  // one destination at a time. Keep each destination contiguous so operator
  // move controls match the executable order they mutate.
  const generating = await getGeneratingItems(
    context,
    // "restricted" scope reads everything — leave those job views unfiltered
    // so still-running work survives its source entity being deleted.
    options.visibilityScope && options.visibilityScope !== "restricted"
      ? entitiesByKey
      : undefined,
  );
  summary.generating = generating.length;

  const failures = Array.from(entitiesByKey.values())
    .filter((entity) => entity.status === "failed")
    .map((entity) => {
      const retry = retryTracker.getRetryInfo(entity.entityId);
      return {
        entityId: entity.entityId,
        entityType: entity.entityType,
        title: entity.title,
        error: entity.error ?? retry?.lastError ?? "Publication failed",
        retryCount: retry?.retryCount ?? 0,
      };
    })
    .sort((left, right) => left.title.localeCompare(right.title));

  summary.needsOperator = summary.draft + summary.failed;

  return publicationPipelineSnapshotSchema.parse({
    summary,
    queue,
    generating,
    failures,
    publishableEntityTypes,
  });
}

async function getGeneratingItems(
  context: ServicePluginContext,
  visibleEntities: ReadonlyMap<string, PublicationEntityItem> | undefined,
): Promise<PublicationJobItem[]> {
  const generating: PublicationJobItem[] = [];

  for (const job of await context.jobs.getActiveJobs()) {
    if (job.source !== "content-pipeline") continue;
    if (job.status !== "pending" && job.status !== "processing") continue;

    let payload: unknown;
    try {
      payload = JSON.parse(job.data);
    } catch {
      continue;
    }
    const parsed = generatingJobDataSchema.safeParse(payload);
    if (!parsed.success) continue;
    if (
      visibleEntities &&
      !visibleEntities.has(
        entityKey(parsed.data.sourceEntityType, parsed.data.sourceEntityId),
      )
    ) {
      continue;
    }

    generating.push({
      id: job.id,
      label: parsed.data.attachmentType ?? job.type,
      target: `${parsed.data.sourceEntityType}/${parsed.data.sourceEntityId}`,
      status: job.status,
    });
  }

  return generating;
}

function entityKey(entityType: string, entityId: string): string {
  return `${entityType}\0${entityId}`;
}

/**
 * Whether a metadata status is one of the pipeline's publication statuses —
 * the same predicate the snapshot uses to admit queue rows, shared so view
 * consumers (e.g. reorder translation) filter identically.
 */
export function hasPublicationStatus(status: unknown): boolean {
  return publicationStatusSchema.safeParse(status).success;
}

function getEntityTitle(
  entityId: string,
  metadata: Record<string, unknown>,
): string {
  for (const key of ["title", "subject", "slug"]) {
    const value = metadata[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return entityId;
}
